import React, { FC, useEffect, useState } from 'react';
import _ from 'lodash';
import { connect } from 'react-redux';
import { useIntl } from 'react-intl';
import { LoadingOutlined } from '@ant-design/icons';
import { Button } from 'antd';
import { ColProps } from 'antd/lib/col';

import { BaseStore } from 'src/store';
import { useLocale } from 'src/modules/locale';
import { LayoutCardList, LayoutCardType } from 'src/modules/layout';
import { GlobalIcon, IconImage } from 'src/modules/global';
import { colInstagramCardPropsMagazinePage } from 'src/utils/grid';

import { fetchArticlesInstagramData } from '../../store/actions';
import { ArticlesInstagramLocalizedEntity, ArticlesInstagramEntity } from '../../types';
import ArticlesInstagramDetailsOverlay from '../overlay/ArticlesInstagramDetailsOverlay';
import styles from './ArticlesInstagramOverview.module.scss';

type NumPosts = number | 'all';

interface ArticleInstagramOverviewProps {
  instagrams: ArticlesInstagramEntity[];
  // TODO remove instagramsSSG once we move the whole thing to SSG. Right now
  // we're only doing SSG for the homepage
  instagramsSSG?: ArticlesInstagramEntity[];
  colProps: ColProps;
  /** How many posts to display */
  numPosts: NumPosts;
  fetchInstagrams: (pagination?: boolean) => void;
  isLoaded: boolean;
  isLoading: boolean;
  displayShowMore: boolean;
}

const ArticlesInstagramOverview: FC<ArticleInstagramOverviewProps> = (props) => {
  const { getLocalizedObject } = useLocale();
  const [
    selectedInstagram,
    setSelectedInstagram,
  ] = useState<null | ArticlesInstagramLocalizedEntity>(null);
  const intl = useIntl();

  const {
    instagrams: instagramsSSR,
    instagramsSSG,
    instagramsCount,
  }: {
    instagrams: ArticlesInstagramLocalizedEntity[];
    instagramsSSG: ArticlesInstagramLocalizedEntity[];
    instagramsCount: number;
  } = getLocalizedObject(props);
  const {
    fetchInstagrams,
    numPosts = 'all',
    isLoaded,
    isLoading,
    displayShowMore = false,
    colProps = colInstagramCardPropsMagazinePage,
  } = props;

  // TODO remove this once we move the whole instagrams fetching to SSG
  const instagrams = _.isEmpty(instagramsSSG) ? instagramsSSR : instagramsSSG;

  useEffect(() => {
    // TODO remove this once we move the whole instagrams fetching to SSG
    if (!_.isEmpty(instagrams)) return;

    if (!isLoading && !isLoaded) {
      fetchInstagrams();
    }
  }, [isLoaded, isLoading, fetchInstagrams, instagrams]);

  if (!instagrams.length) return null;

  const moreInstagramsAvailable = !isLoading && instagrams.length < instagramsCount;

  let instagramsTrimmed = instagrams;
  if (numPosts !== 'all') instagramsTrimmed = instagrams.slice(0, numPosts);

  return (
    <div className={styles.cardContainer}>
      <LayoutCardList
        colProps={colProps}
        type={LayoutCardType.Instagram}
        items={instagramsTrimmed.map((instagram) => ({
          image: instagram?.media[0],
          onClick: () => setSelectedInstagram(instagram),
        }))}
      />
      <ArticlesInstagramDetailsOverlay
        data={selectedInstagram}
        onClose={() => setSelectedInstagram(null)}
      />
      <div className={styles.seeMore}>
        {isLoading && <LoadingOutlined />}
        {displayShowMore && moreInstagramsAvailable && (
          <Button type="link" onClick={() => fetchInstagrams(true)}>
            {intl.formatMessage({ id: 'page/magazine/listing/see_more' })}
            <GlobalIcon className={styles.seeMoreIcon} image={IconImage.Plus} />
          </Button>
        )}
      </div>
    </div>
  );
};

const mapStateToProps = (state: BaseStore) => {
  return {
    ...state.articles.instagrams,
  };
};

const mapDispatchToProps = (dispatch) => {
  return {
    dispatch,
    fetchInstagrams: (pagination = false) => dispatch(fetchArticlesInstagramData(pagination)),
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(ArticlesInstagramOverview);
